import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings as SettingsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Toujours actif
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà donné son consentement
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Afficher le banner après 1 seconde
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Charger les préférences sauvegardées
      try {
        // SÉCURITÉ #26: Validation stricte préférences cookies
        const saved = JSON.parse(consent);
        
        // Valider structure et types pour éviter injection/corruption
        if (saved && 
            typeof saved === 'object' && 
            !Array.isArray(saved) &&
            typeof saved.necessary === 'boolean' &&
            typeof saved.functional === 'boolean' &&
            typeof saved.analytics === 'boolean') {
          setPreferences(saved);
          applyPreferences(saved);
        } else {
          logger.warn('Invalid cookie preferences structure, resetting');
          localStorage.removeItem('cookie_consent');
          setTimeout(() => setShowBanner(true), 1000);
        }
      } catch (e) {
        logger.error('Erreur parsing cookie_consent:', e);
        localStorage.removeItem('cookie_consent'); // Reset si corrompu
        setTimeout(() => setShowBanner(true), 1000);
      }
    }
  }, []);

  const applyPreferences = (prefs: CookiePreferences) => {
    // Appliquer les préférences (activer/désactiver Google Analytics, etc.)
    if (prefs.analytics && typeof window !== 'undefined') {
      // Activer Google Analytics (si configuré)
      // @ts-ignore
      if (window.gtag) {
        // @ts-ignore
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    } else if (typeof window !== 'undefined') {
      // Désactiver Google Analytics
      // @ts-ignore
      if (window.gtag) {
        // @ts-ignore
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    }
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    applyPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
    };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const saveCustomPreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-theme-base/60 backdrop-blur-sm z-[999]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Banner simple */}
      {!showSettings && (
        <div className="cookie-banner">
          <div className="cookie-banner-inner">
              <h3>Cookies</h3>
              <p>
                    Nous utilisons des cookies pour le fonctionnement du site. Les autres nécessitent votre accord.
                    <Link to="/cookies" className="text-[var(--spark-gold)] ml-1">En savoir plus</Link>
              </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={acceptAll}
                      className="px-6 py-2.5 btn-modern btn-primary"
                    >
                      Tout accepter
                    </button>
                    <button
                      onClick={acceptNecessary}
                      className="px-6 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors border-2 border-gray-200 dark:border-gray-700"
                    >
                      Nécessaires uniquement
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-6 py-2.5 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors inline-flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                    >
                      <SettingsIcon size={16} />
                      Personnaliser
                    </button>
                  </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[1000] p-4 md:p-6 animate-slide-up">
          <div className="max-w-3xl mx-auto bg-[var(--theme-elevated)] border border-theme">
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Cookie className="w-6 h-6 text-[var(--spark-gold)]" />
                  <h3 className="text-xl font-display text-theme-primary">
                    Paramètres des cookies
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Cookie categories */}
              <div className="space-y-4 mb-6">
                {/* Nécessaires */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Cookies nécessaires</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Indispensables au fonctionnement du site (authentification, sécurité)
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full">
                        Toujours actif
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fonctionnels */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Cookies fonctionnels</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Mémorisation de vos préférences (thème, langue, interface)
                      </p>
                    </div>
                    <label className="flex-shrink-0 ml-4 relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>

                {/* Analytiques */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Cookies analytiques</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        Mesure d'audience et statistiques (Google Analytics, anonymisé)
                      </p>
                    </div>
                    <label className="flex-shrink-0 ml-4 relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-wrap gap-3 pt-4 border-t-2 border-gray-200 dark:border-gray-700">
                <button
                  onClick={saveCustomPreferences}
                  className="flex-1 px-6 py-2.5 btn-modern btn-primary"
                >
                  Enregistrer mes préférences
                </button>
                <button
                  onClick={acceptAll}
                  className="px-6 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors border-2 border-gray-200 dark:border-gray-700"
                >
                  Tout accepter
                </button>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 text-center mt-4">
                Consultez notre{' '}
                <Link to="/cookies" className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
                  Politique de Cookies
                </Link>
                {' '}pour plus d'informations
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
}

